import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { asArray, asCollection } from '../lib/collections'
import {
    monthlyBriefSlideStorageId,
    filterMonthlyBriefImagesByKind,
    sortMonthlyBriefImagesBySlideOrder,
    findSlideMetaForMonthlyBriefImage,
    parseStorageSlideIdFromMonthlyBriefImageName,
} from '../lib/monthlyBriefCardNews'
import { formatApiDateTimeDisplay, formatDateTimeMinute } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import { PageHeader } from '../ui/PageLayout'
import MenuGuideButton from '../components/MenuGuideButton'
import { AiFeatureBadge, AiGeneratedBadge } from '../components/AiDisclosure'

/** 백엔드 InsurerNewsletterSummaryService.isSuccessfulSummary 와 동일한 판별 */
const INSURER_NEWSLETTER_SUMMARY_FAILURE_PREFIX = '요약 생성 실패:'

function isInsurerNewsletterSummaryFailureText(text) {
    if (text == null) return false
    return String(text).trimStart().startsWith(INSURER_NEWSLETTER_SUMMARY_FAILURE_PREFIX)
}

/** 일반 사용자에게는 실패 원문 대신 빈 값으로 두고, UI에서 준비 중 문구로 대체 */
function insurerNewsletterExposureTextForUser(raw, isOperator) {
    if (isOperator) return raw == null ? '' : String(raw)
    const s = raw == null ? '' : String(raw).trim()
    if (!s || isInsurerNewsletterSummaryFailureText(s)) return ''
    return String(raw)
}

const INSURER_NEWSLETTER_USER_PREPARING_SUMMARY = '요약을 준비중입니다.'

function normalizeInstantStr(v) {
    if (v == null) return null
    return String(v)
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/** 백그라운드 요약 완료 시점: summaryGeneratedAt 이 요청 직전 baseline 과 달라짐 */
async function pollInsurerNewsletterSummary(api, id, baselineSummaryGeneratedAt, { intervalMs = 10_000, maxMs = 15 * 60 * 1000 } = {}) {
    const base = normalizeInstantStr(baselineSummaryGeneratedAt)
    const started = Date.now()
    while (Date.now() - started < maxMs) {
        await sleep(intervalMs)
        try {
            const res = await api.get(`/insurer-newsletters/${id}/summary`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const genAt = normalizeInstantStr(payload?.summaryGeneratedAt)
            if (genAt != null && genAt !== base) {
                return { ok: true, payload }
            }
        } catch {
            /* 다음 주기에 재시도 */
        }
    }
    return { ok: false }
}

/** 백그라운드 이미지: 최초 없음 → exists, 또는 createdAt 변경 */
async function pollInsurerNewsletterGeneratedImage(api, id, baselineExists, baselineCreatedAt, { intervalMs = 10_000, maxMs = 15 * 60 * 1000 } = {}) {
    const baseCreated = normalizeInstantStr(baselineCreatedAt)
    const started = Date.now()
    while (Date.now() - started < maxMs) {
        await sleep(intervalMs)
        try {
            const res = await api.get(`/insurer-newsletters/${id}/generated-image`)
            const info = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (info?.exists) {
                if (!baselineExists) return { ok: true, info }
                const c = normalizeInstantStr(info.createdAt)
                if (c != null && c !== baseCreated) return { ok: true, info }
            }
        } catch {
            /* 다음 주기에 재시도 */
        }
    }
    return { ok: false }
}

export default function InsurerNewslettersPage() {
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    // 2026년 1월부터 시작
    const MIN_YEAR = 2026
    const MIN_MONTH = 1
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1
    
    // 초기값: 2026년 1월보다 이전이면 2026년 1월로 설정
    const initialYear = Math.max(MIN_YEAR, currentYear)
    const initialMonth = initialYear === MIN_YEAR ? Math.max(MIN_MONTH, currentMonth) : currentMonth
    
    const [year, setYear] = useState(initialYear)
    const [month, setMonth] = useState(initialMonth)
    const [uploading, setUploading] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [batchSummarizing, setBatchSummarizing] = useState(false)
    const [batchGeneratingImage, setBatchGeneratingImage] = useState(false)
    const [selectedImageModel, setSelectedImageModel] = useState('gemini-3.1-flash-image')
    /** 소식지·월간 브리프 카드 이미지 공통: 'gemini' | 'openai' */
    const [newsletterImageProvider, setNewsletterImageProvider] = useState('gemini')
    const [openaiImageQuality, setOpenaiImageQuality] = useState('medium')
    const [openaiImageSize, setOpenaiImageSize] = useState('1024x1024')
    const [imageProcessType, setImageProcessType] = useState('플라이어')
    const [imageStyle, setImageStyle] = useState('충격통계')
    const [imageAudience, setImageAudience] = useState('전체')
    const [imageTone, setImageTone] = useState('친근하고 신뢰감 있는 톤')
    const [imageCardCount, setImageCardCount] = useState('1')
    const [imageRatio, setImageRatio] = useState('1:1')
    const [summaryModalOpen, setSummaryModalOpen] = useState(false)
    const [summaryLoading, setSummaryLoading] = useState(false)
    const [summaryData, setSummaryData] = useState(null)
    /** 'exposure' | 'aiFinal' | 'gpt' | 'gemini' — 노출(직접·자동 요약 우선순위) / 중재 / 초안 */
    const [summaryViewTab, setSummaryViewTab] = useState('exposure')
    const [manualDraft, setManualDraft] = useState('')
    const [manualSaving, setManualSaving] = useState(false)
    const [currentNewsletterId, setCurrentNewsletterId] = useState(null)
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [imageModalLoading, setImageModalLoading] = useState(false)
    const [imageModalUrl, setImageModalUrl] = useState('')
    const [imageModalName, setImageModalName] = useState('')
    /** 생명/손해 섹션: 보험사별 요약+이미지 통합 보기 */
    const [sectionBrowseOpen, setSectionBrowseOpen] = useState(false)
    const [sectionBrowseType, setSectionBrowseType] = useState(null) // 'LIFE' | 'FIRE'
    const [sectionBrowseList, setSectionBrowseList] = useState([])
    const [sectionBrowseIndex, setSectionBrowseIndex] = useState(0)
    const [sectionBrowseLoading, setSectionBrowseLoading] = useState(false)
    const [sectionBrowseSummaryText, setSectionBrowseSummaryText] = useState('')
    const [sectionBrowseImageUrl, setSectionBrowseImageUrl] = useState('')
    const [sectionBrowseImageName, setSectionBrowseImageName] = useState('')
    const [imageGenLoadingId, setImageGenLoadingId] = useState(null)
    const fileInputRef = React.useRef(null)

    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const imageModelOptions = [
        { value: 'gemini-3.1-flash-image', label: 'Gemini 3.1 Flash Image (권장)' },
        { value: 'gemini-2.5-flash-image', label: 'Gemini 2.5 Flash Image' },
        { value: 'gemini-3-pro-image', label: 'Gemini 3 Pro Image' }
    ]
    const imageModelSelectOptions = newsletterImageProvider === 'openai'
        ? [{ value: 'gpt-image-2', label: 'GPT Image 2 (OpenAI)' }]
        : imageModelOptions
    const openAiQualityOptions = ['low', 'medium', 'high', 'auto']
    const openAiSizeOptions = [
        '1024x1024', '1024x1536', '1536x1024', '2048x2048', '2048x1152', '3840x2160', '2160x3840', 'auto'
    ]

    // 생명보험과 손해보험을 동시에 조회
    const lifeQuery = useQuery({
        queryKey: ['insurer-newsletters', { year, month, type: 'LIFE' }],
        queryFn: async () => {
            const params = { year, month, type: 'LIFE' }
            const res = await api.get('/insurer-newsletters', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            return { items: raw }
        },
        enabled: !!year && !!month,
        keepPreviousData: true,
    })

    const fireQuery = useQuery({
        queryKey: ['insurer-newsletters', { year, month, type: 'FIRE' }],
        queryFn: async () => {
            const params = { year, month, type: 'FIRE' }
            const res = await api.get('/insurer-newsletters', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            return { items: raw }
        },
        enabled: !!year && !!month,
        keepPreviousData: true,
    })

    const lifeItems = lifeQuery.data?.items || []
    const fireItems = fireQuery.data?.items || []
    const loading = lifeQuery.isFetching || fireQuery.isFetching
    const error = lifeQuery.isError || fireQuery.isError ? '목록을 불러오지 못했습니다' : null

    // ── 월간 이슈 브리프 ──
    const [briefExpanded, setBriefExpanded] = useState(true)
    const [briefGenerating, setBriefGenerating] = useState(false)
    const [briefBriefTab, setBriefBriefTab] = useState('life') // 'life' | 'fire'
    const [briefCardImageLoading, setBriefCardImageLoading] = useState(null)
    const [briefBatchImageLoading, setBriefBatchImageLoading] = useState(false)
    // 카드뉴스 팝업
    const [cardNewsModalOpen, setCardNewsModalOpen] = useState(false)
    const [cardNewsKind, setCardNewsKind] = useState('life') // 'life' | 'fire' — 생보/손보 카드뉴스 분리
    const [cardNewsImages, setCardNewsImages] = useState([])
    const [cardNewsIndex, setCardNewsIndex] = useState(0)
    const [cardNewsLoading, setCardNewsLoading] = useState(false)
    const [cardNewsDownloading, setCardNewsDownloading] = useState(false)
    // 슬라이드별 이미지 캐시 { slideId: { fileId, blobUrl } }
    const [slideImageMap, setSlideImageMap] = useState({})

    const briefQuery = useQuery({
        queryKey: ['monthly-brief', { year, month }],
        queryFn: async () => {
            const res = await api.get('/insurer-newsletter-monthly-briefs', { params: { year, month } })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        enabled: !!year && !!month,
        keepPreviousData: true,
    })
    const briefData = briefQuery.data

    // 브리프 카드이미지 목록 조회
    const cardImagesQuery = useQuery({
        queryKey: ['monthly-brief-card-images', briefData?.id],
        queryFn: async () => {
            const res = await api.get(`/insurer-newsletter-monthly-briefs/${briefData.id}/card-images`)
            return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || [])
        },
        enabled: !!briefData?.id && briefData?.status === 'COMPLETED',
        keepPreviousData: true,
    })

    // 카드이미지 조회 결과로 슬라이드별 fileId 매핑 갱신 (키: life_slide-1 등 저장용 slideId)
    React.useEffect(() => {
        const items = cardImagesQuery.data
        if (!Array.isArray(items)) return
        const map = {}
        for (const img of items) {
            const sid = parseStorageSlideIdFromMonthlyBriefImageName(img.imageName)
            if (sid && img.fileId) {
                if (!map[sid] || new Date(img.createdAt) > new Date(map[sid].createdAt)) {
                    map[sid] = { fileId: img.fileId, createdAt: img.createdAt }
                }
            }
        }
        setSlideImageMap(prev => {
            const next = { ...prev }
            for (const [sid, info] of Object.entries(map)) {
                next[sid] = { fileId: info.fileId }
            }
            return next
        })
    }, [cardImagesQuery.data])

    const handleGenerateBrief = async () => {
        setBriefGenerating(true)
        try {
            await api.post('/insurer-newsletter-monthly-briefs/generate', null, { params: { year, month } })
            show('월간 이슈 브리프 생성이 시작되었습니다. 잠시 후 새로고침해주세요.', 'success')
            setTimeout(() => briefQuery.refetch(), 5000)
            setTimeout(() => briefQuery.refetch(), 15000)
            setTimeout(() => briefQuery.refetch(), 30000)
            setTimeout(() => { briefQuery.refetch(); setBriefGenerating(false) }, 60000)
        } catch (err) {
            show('브리프 생성 요청 실패', 'error')
            setBriefGenerating(false)
        }
    }

    const handleToggleBriefPublish = async () => {
        if (!briefData?.id) return
        try {
            await api.put(`/insurer-newsletter-monthly-briefs/${briefData.id}/publish`, { published: !briefData.published })
            briefQuery.refetch()
            show(briefData.published ? '비공개로 전환했습니다' : '공개로 전환했습니다', 'success')
        } catch { show('상태 변경 실패', 'error') }
    }

    const handleGenerateCardImage = async (slideId, slideTitle, slideBody, briefType) => {
        if (!briefData?.id) return
        setBriefCardImageLoading(slideId)
        try {
            await api.post(`/insurer-newsletter-monthly-briefs/${briefData.id}/generate-card-image`, {
                slideId, slideTitle, slideBody, briefType,
                model: newsletterImageProvider === 'openai' ? 'gpt-image-2' : (selectedImageModel || undefined),
                imageProvider: newsletterImageProvider === 'openai' ? 'openai' : 'gemini',
                ...(newsletterImageProvider === 'openai'
                    ? { openaiImageQuality: openaiImageQuality || 'medium', openaiImageSize: openaiImageSize || '1024x1024' }
                    : {}),
            })
            show('카드뉴스 이미지 생성이 시작되었습니다.', 'success')
            setTimeout(() => cardImagesQuery.refetch(), 15000)
            setTimeout(() => cardImagesQuery.refetch(), 45000)
        } catch { show('카드뉴스 이미지 생성 실패', 'error') }
        setBriefCardImageLoading(null)
    }

    const handleBatchGenerateCardImages = async (briefType) => {
        if (!briefData?.id || !briefData.payload) return
        const brief = briefType === 'life' ? briefData.payload.lifeBrief : briefData.payload.fireBrief
        if (!brief || !Array.isArray(brief.slides) || brief.slides.length === 0) {
            show('슬라이드가 없습니다', 'error')
            return
        }
        setBriefBatchImageLoading(true)
        try {
            const tab = briefType === 'life' ? 'life' : 'fire'
            const slides = brief.slides.map((s, idx) => ({
                slideId: monthlyBriefSlideStorageId(tab, s, idx),
                slideTitle: s.title,
                slideBody: s.body,
                briefType: briefType === 'life' ? 'LIFE' : 'FIRE'
            }))
            await api.post(`/insurer-newsletter-monthly-briefs/${briefData.id}/batch-generate-card-images`, {
                slides,
                model: newsletterImageProvider === 'openai' ? 'gpt-image-2' : (selectedImageModel || undefined),
                imageProvider: newsletterImageProvider === 'openai' ? 'openai' : 'gemini',
                ...(newsletterImageProvider === 'openai'
                    ? { openaiImageQuality: openaiImageQuality || 'medium', openaiImageSize: openaiImageSize || '1024x1024' }
                    : {}),
            })
            show(`${slides.length}개 카드뉴스 이미지 일괄 생성이 시작되었습니다.`, 'success')
            setTimeout(() => cardImagesQuery.refetch(), 20000)
            setTimeout(() => cardImagesQuery.refetch(), 60000)
            setTimeout(() => cardImagesQuery.refetch(), 120000)
        } catch { show('일괄 이미지 생성 실패', 'error') }
        setBriefBatchImageLoading(false)
    }

    const openCardNewsModal = async (kind) => {
        if (!briefData?.id) return
        setCardNewsKind(kind === 'fire' ? 'fire' : 'life')
        setCardNewsModalOpen(true)
        setCardNewsLoading(true)
        setCardNewsIndex(0)
        try {
            const res = await api.get(`/insurer-newsletter-monthly-briefs/${briefData.id}/card-images`)
            const items = (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data || [])
            const filtered = sortMonthlyBriefImagesBySlideOrder(
                filterMonthlyBriefImagesByKind(items, kind === 'fire' ? 'fire' : 'life'),
                briefData.payload,
                kind === 'fire' ? 'fire' : 'life'
            )
            const images = []
            for (const item of filtered) {
                if (!item.fileId) continue
                try {
                    const imgRes = await api.get(`/files/${item.fileId}/preview`, { responseType: 'blob' })
                    const blobUrl = URL.createObjectURL(imgRes.data)
                    images.push({ ...item, blobUrl })
                } catch {
                    images.push({ ...item, blobUrl: null })
                }
            }
            setCardNewsImages(images)
        } catch { show('카드뉴스 이미지를 불러올 수 없습니다', 'error') }
        setCardNewsLoading(false)
    }

    const closeCardNewsModal = () => {
        for (const img of cardNewsImages) {
            if (img.blobUrl) try { URL.revokeObjectURL(img.blobUrl) } catch {}
        }
        setCardNewsModalOpen(false)
        setCardNewsImages([])
        setCardNewsIndex(0)
        setCardNewsKind('life')
        setCardNewsDownloading(false)
    }

    const downloadCurrentCardNewsImage = async () => {
        const cur = cardNewsImages[cardNewsIndex]
        if (!cur?.fileId) {
            show('다운로드할 이미지가 없습니다.', 'error')
            return
        }
        const fallbackName = `카드뉴스_${year}년${month}월_${cardNewsKind === 'fire' ? '손보' : '생보'}_${cardNewsIndex + 1}.png`
        const rawName = cur.imageName != null ? String(cur.imageName) : ''
        const safeName = rawName.replace(/[/\\?*:|"<>]/g, '_').trim()
        const downloadName = safeName.length > 0 ? safeName : fallbackName

        setCardNewsDownloading(true)
        try {
            const res = await api.get(`/files/${cur.fileId}/download`, { responseType: 'blob' })
            const blobUrl = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = downloadName
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(blobUrl)
            show('이미지를 다운로드했습니다.', 'success')
        } catch {
            show('이미지 다운로드에 실패했습니다.', 'error')
        } finally {
            setCardNewsDownloading(false)
        }
    }

    const handlePreview = async (id) => {
        try {
            const res = await api.get(`/insurer-newsletters/${id}/preview`, {
                responseType: 'blob'
            })
            const blobUrl = URL.createObjectURL(res.data)
            window.open(blobUrl, '_blank', 'noopener,noreferrer')
            // 60초 후 URL 해제
            setTimeout(() => {
                try { URL.revokeObjectURL(blobUrl) } catch { }
            }, 60000)
        } catch (err) {
            show('미리보기에 실패했습니다', 'error')
        }
    }

    const handleDownload = async (id, filename) => {
        try {
            const res = await api.get(`/insurer-newsletters/${id}/download`, {
                responseType: 'blob'
            })
            const blobUrl = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = filename || 'download.pdf'
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(blobUrl)
            show('다운로드되었습니다', 'success')
        } catch (err) {
            show('다운로드에 실패했습니다', 'error')
        }
    }

    const handleUpload = async (e, type) => {
        const files = e.target.files
        if (!files || files.length === 0) return

        setUploading(true)
        try {
            const formData = new FormData()
            formData.append('year', year)
            formData.append('month', month)
            formData.append('type', type)
            for (let i = 0; i < files.length; i++) {
                formData.append('files', files[i])
            }

            const res = await api.post('/insurer-newsletters/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const successCount = payload?.successCount || 0
            const errors = asArray(payload?.errors)

            if (successCount > 0) {
                show(`${successCount}개 파일이 업로드되었습니다`, 'success')
                lifeQuery.refetch()
                fireQuery.refetch()
            }
            if (errors.length > 0) {
                show(`일부 파일 업로드 실패: ${errors.join(', ')}`, 'warning')
            }
        } catch (err) {
            const message = err?.response?.data?.message || '업로드에 실패했습니다'
            show(message, 'error')
        } finally {
            setUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleSync = async (type) => {
        setSyncing(true)
        try {
            const res = await api.post('/insurer-newsletters/sync', {
                year,
                month,
                type
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const syncedCount = payload?.syncedCount || 0
            const errors = asArray(payload?.errors)

            if (syncedCount > 0) {
                show(`${syncedCount}개 파일이 동기화되었습니다`, 'success')
                lifeQuery.refetch()
                fireQuery.refetch()
            } else {
                show('동기화할 파일이 없습니다', 'info')
            }
            if (errors.length > 0) {
                show(`일부 파일 동기화 실패: ${errors.join(', ')}`, 'warning')
            }
        } catch (err) {
            const message = err?.response?.data?.message || '동기화에 실패했습니다'
            show(message, 'error')
        } finally {
            setSyncing(false)
        }
    }

    const handleDelete = async (id) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        try {
            await api.delete(`/insurer-newsletters/${id}`)
            show('삭제되었습니다', 'success')
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            show('삭제에 실패했습니다', 'error')
        }
    }

    const handleBatchSummary = async (type) => {
        if (!window.confirm(`${type === 'LIFE' ? '생명보험' : '손해보험'} 소식지 전체에 대해 요약을 생성하시겠습니까?`)) {
            return
        }
        
        setBatchSummarizing(true)
        try {
            const res = await api.post('/insurer-newsletters/batch-generate-summary', null, {
                params: { year, month, type }
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const totalCount = payload?.totalCount || 0
            const queuedCount = payload?.queuedCount
            if (totalCount === 0) {
                show('요약할 소식지가 없습니다', 'info')
            } else if (res.status === 202 || payload?.status === 'STARTED') {
                const n = queuedCount != null ? queuedCount : totalCount
                show(`일괄 요약을 백그라운드에서 시작했습니다(대상 ${n}건). 잠시 후 목록을 새로고침해 주세요.`, 'info')
            } else {
                const successCount = payload?.successCount || 0
                const failedCount = payload?.failedCount || 0
                show(`요약 완료: 성공 ${successCount}개, 실패 ${failedCount}개`,
                    failedCount > 0 ? 'warning' : 'success')
            }
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message || '일괄 요약 생성에 실패했습니다'
            show(message, 'error')
        } finally {
            setBatchSummarizing(false)
        }
    }

    const handleViewSummary = async (id) => {
        setCurrentNewsletterId(id)
        setSummaryModalOpen(true)
        setSummaryLoading(true)
        setSummaryData(null)
        setManualDraft('')
        try {
            const res = await api.get(`/insurer-newsletters/${id}/summary`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const allItems = [...lifeItems, ...fireItems]
            const newsletter = allItems.find(item => item.id === id)
            setSummaryViewTab('exposure')
            setSummaryData({
                effectiveSummary: payload.effectiveSummary,
                finalArbitrated: payload.summaryArbitrated,
                'gpt-4o-mini': payload.summaryGpt4oMini,
                gemini: payload.summaryGemini,
                summaryManual: payload.summaryManual,
                summaryManualAt: payload.summaryManualAt,
                summaryManualUpdatedByEmail: payload.summaryManualUpdatedByEmail,
                textSource: payload.summaryTextSource,
                insurerName: newsletter?.insurerName || '알 수 없음',
                year: newsletter?.year || year,
                month: newsletter?.month || month
            })
            setManualDraft(payload.summaryManual || '')
        } catch (err) {
            const message = err?.response?.data?.message || '요약 조회에 실패했습니다'
            show(message, 'error')
            setSummaryModalOpen(false)
        } finally {
            setSummaryLoading(false)
        }
    }

    const handleSaveManualSummary = async () => {
        if (!currentNewsletterId) return
        setManualSaving(true)
        try {
            const res = await api.put(`/insurer-newsletters/${currentNewsletterId}/summary-manual`, { text: manualDraft })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            setSummaryData((prev) => prev ? {
                ...prev,
                effectiveSummary: payload.effectiveSummary,
                summaryManual: payload.summaryManual,
                summaryManualAt: payload.summaryManualAt ?? null,
                summaryManualUpdatedByEmail: payload.summaryManualUpdatedByEmail ?? null,
            } : prev)
            show('직접 요약을 저장했습니다', 'success')
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message || '저장에 실패했습니다'
            show(message, 'error')
        } finally {
            setManualSaving(false)
        }
    }

    const handleClearManualSummary = async () => {
        if (!currentNewsletterId) return
        if (!window.confirm('직접 요약을 삭제할까요? 노출은 요약 기준으로 돌아갑니다.')) return
        setManualSaving(true)
        try {
            const res = await api.delete(`/insurer-newsletters/${currentNewsletterId}/summary-manual`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            setManualDraft('')
            setSummaryData((prev) => prev ? {
                ...prev,
                effectiveSummary: payload.effectiveSummary,
                summaryManual: payload.summaryManual ?? null,
                summaryManualAt: payload.summaryManualAt ?? null,
                summaryManualUpdatedByEmail: payload.summaryManualUpdatedByEmail ?? null,
            } : prev)
            show('직접 요약을 삭제했습니다', 'success')
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message || '삭제에 실패했습니다'
            show(message, 'error')
        } finally {
            setManualSaving(false)
        }
    }

    const handleAiSummary = async (item) => {
        const id = item.id
        const baselineSummaryAt = item.summaryGeneratedAt
        setSummaryLoading(true)
        setCurrentNewsletterId(id)
        setSummaryModalOpen(true)
        setSummaryData(null)
        const allItems = [...lifeItems, ...fireItems]
        const newsletter = allItems.find((row) => row.id === id)
        const applySummaryPayload = (payload) => {
            setSummaryViewTab('exposure')
            setSummaryData({
                effectiveSummary: payload.effectiveSummary,
                finalArbitrated: payload.summaryArbitrated,
                'gpt-4o-mini': payload.summaryGpt4oMini,
                gemini: payload.summaryGemini,
                summaryManual: payload.summaryManual,
                summaryManualAt: payload.summaryManualAt,
                summaryManualUpdatedByEmail: payload.summaryManualUpdatedByEmail,
                textSource: payload.summaryTextSource,
                insurerName: newsletter?.insurerName || '알 수 없음',
                year: newsletter?.year || year,
                month: newsletter?.month || month
            })
            setManualDraft(payload.summaryManual || '')
        }
        try {
            await api.post(`/insurer-newsletters/${id}/generate-summary`)
            show('요약 생성을 시작했습니다. 완료될 때까지 잠시 기다려 주세요.', 'info')
            const polled = await pollInsurerNewsletterSummary(api, id, baselineSummaryAt)
            if (!polled.ok) {
                show('요약 생성이 시간 내에 완료되지 않았습니다. 목록을 새로고침한 뒤 요약 보기를 확인해 주세요.', 'error')
                setSummaryModalOpen(false)
                lifeQuery.refetch()
                fireQuery.refetch()
                return
            }
            applySummaryPayload(polled.payload)
            show('요약이 생성되었습니다', 'success')
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message || '요약 생성에 실패했습니다'
            show(message, 'error')
            setSummaryModalOpen(false)
        } finally {
            setSummaryLoading(false)
        }
    }

    const handleGenerateImage = async (id) => {
        let baselineExists = false
        let baselineCreatedAt = null
        try {
            const infoRes = await api.get(`/insurer-newsletters/${id}/generated-image`)
            const info = (infoRes?.data && infoRes.data.data !== undefined) ? infoRes.data.data : infoRes.data
            baselineExists = !!info?.exists
            baselineCreatedAt = info?.createdAt ?? null
        } catch {
            /* 선행 조회 실패 시에도 생성 요청은 시도 */
        }
        setImageGenLoadingId(id)
        try {
            await api.post(`/insurer-newsletters/${id}/generate-image`, {
                model: newsletterImageProvider === 'openai' ? 'gpt-image-2' : selectedImageModel,
                imageProvider: newsletterImageProvider === 'openai' ? 'openai' : 'gemini',
                ...(newsletterImageProvider === 'openai'
                    ? { openaiImageQuality: openaiImageQuality || 'medium', openaiImageSize: openaiImageSize || '1024x1024' }
                    : {}),
                processType: imageProcessType,
                style: imageStyle,
                audience: imageAudience,
                tone: imageTone,
                cardCount: imageCardCount,
                ratio: imageRatio
            })
            show('이미지 생성을 시작했습니다. 완료될 때까지 잠시 기다려 주세요.', 'info')
            const polled = await pollInsurerNewsletterGeneratedImage(api, id, baselineExists, baselineCreatedAt)
            if (!polled.ok) {
                show('이미지 생성이 시간 내에 완료되지 않았습니다. 목록을 새로고침 후 이미지 보기를 확인해 주세요.', 'warning')
            } else {
                show(isOperator ? 'AI 이미지가 생성되었습니다' : '이미지가 생성되었습니다', 'success')
            }
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message
                || (isOperator ? 'AI 이미지 생성에 실패했습니다' : '이미지 생성에 실패했습니다')
            show(message, 'error')
        } finally {
            setImageGenLoadingId(null)
        }
    }

    const handleBatchGenerateImage = async (type) => {
        const imageBatchConfirm = isOperator
            ? `${type === 'LIFE' ? '생명보험' : '손해보험'} 소식지 전체에 대해 AI 이미지를 생성하시겠습니까?`
            : `${type === 'LIFE' ? '생명보험' : '손해보험'} 소식지 전체에 대해 이미지를 생성하시겠습니까?`
        if (!window.confirm(imageBatchConfirm)) {
            return
        }
        setBatchGeneratingImage(true)
        try {
            const res = await api.post('/insurer-newsletters/batch-generate-image', {
                model: newsletterImageProvider === 'openai' ? 'gpt-image-2' : selectedImageModel,
                imageProvider: newsletterImageProvider === 'openai' ? 'openai' : 'gemini',
                ...(newsletterImageProvider === 'openai'
                    ? { openaiImageQuality: openaiImageQuality || 'medium', openaiImageSize: openaiImageSize || '1024x1024' }
                    : {}),
                processType: imageProcessType,
                style: imageStyle,
                audience: imageAudience,
                tone: imageTone,
                cardCount: imageCardCount,
                ratio: imageRatio
            }, {
                params: { year, month, type }
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const totalCount = payload?.totalCount ?? 0
            if (totalCount === 0) {
                show('이미지를 생성할 소식지가 없습니다', 'info')
            } else if (payload?.status === 'SKIPPED_ALL') {
                show(payload?.message || '요약이 없어 이미지 생성을 건너뜁니다.', 'info')
            } else if (res.status === 202 || payload?.status === 'STARTED') {
                const q = payload?.queuedCount ?? 0
                const sk = payload?.skippedCount ?? 0
                const msg = sk > 0
                    ? `일괄 이미지 생성을 백그라운드에서 시작했습니다(진행 ${q}건, 요약 없음 건너뜀 ${sk}건). 잠시 후 목록을 새로고침해 주세요.`
                    : `일괄 이미지 생성을 백그라운드에서 시작했습니다(진행 ${q}건). 잠시 후 목록을 새로고침해 주세요.`
                show(msg, 'info')
            } else {
                show(
                    isOperator
                        ? `AI 이미지 생성 완료: 성공 ${payload?.successCount || 0}개, 실패 ${payload?.failedCount || 0}개`
                        : `이미지 생성 완료: 성공 ${payload?.successCount || 0}개, 실패 ${payload?.failedCount || 0}개`,
                    'success'
                )
            }
            lifeQuery.refetch()
            fireQuery.refetch()
        } catch (err) {
            const message = err?.response?.data?.message
                || (isOperator ? '일괄 AI 이미지 생성에 실패했습니다' : '일괄 이미지 생성에 실패했습니다')
            show(message, 'error')
        } finally {
            setBatchGeneratingImage(false)
        }
    }

    const handleViewImage = async (id) => {
        setImageModalLoading(true)
        setImageModalOpen(true)
        setImageModalUrl('')
        setImageModalName('')
        try {
            const infoRes = await api.get(`/insurer-newsletters/${id}/generated-image`)
            const info = (infoRes?.data && infoRes.data.data !== undefined) ? infoRes.data.data : infoRes.data
            if (!info?.exists || !info?.fileId) {
                show('생성된 발송용 이미지가 없습니다', 'info')
                setImageModalOpen(false)
                return
            }
            const res = await api.get(`/files/${info.fileId}/preview`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
            const blobUrl = URL.createObjectURL(blob)
            setImageModalUrl(blobUrl)
            setImageModalName(info?.imageName || '')
        } catch (err) {
            const message = err?.response?.data?.message || '이미지 조회에 실패했습니다'
            show(message, 'error')
            setImageModalOpen(false)
        } finally {
            setImageModalLoading(false)
        }
    }

    /** 보험사명 기준 정렬된 목록 (보험사당 대표 소식지 1건 — 그룹의 첫 행) */
    const buildInsurerBrowseList = (items) => {
        if (!items || items.length === 0) return []
        const grouped = items.reduce((acc, item) => {
            const key = item.insurerName || '매핑 안됨'
            if (!acc[key]) acc[key] = []
            acc[key].push(item)
            return acc
        }, {})
        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b, 'ko'))
            .map(([insurerName, groupItems]) => {
                const first = groupItems[0]
                return {
                    id: first.id,
                    insurerName,
                    year: first.year ?? year,
                    month: first.month ?? month,
                }
            })
    }

    const revokeSectionBrowseImage = () => {
        setSectionBrowseImageUrl((prev) => {
            try {
                if (prev) URL.revokeObjectURL(prev)
            } catch { /* ignore */ }
            return ''
        })
    }

    const loadSectionBrowseAtIndex = async (list, index) => {
        if (!list || list.length === 0 || index < 0 || index >= list.length) return
        const entry = list[index]
        setSectionBrowseLoading(true)
        setSectionBrowseSummaryText('')
        revokeSectionBrowseImage()
        setSectionBrowseImageName('')
        try {
            const [summaryRes, imageInfoRes] = await Promise.all([
                api.get(`/insurer-newsletters/${entry.id}/summary`).catch(() => ({ data: {} })),
                api.get(`/insurer-newsletters/${entry.id}/generated-image`).catch(() => ({ data: {} })),
            ])
            const sumPayload = (summaryRes?.data && summaryRes.data.data !== undefined)
                ? summaryRes.data.data
                : summaryRes.data
            const rawSummary = sumPayload?.effectiveSummary || sumPayload?.summaryArbitrated || sumPayload?.summaryGpt4oMini || ''
            setSectionBrowseSummaryText(insurerNewsletterExposureTextForUser(rawSummary, isOperator))

            const infoPayload = (imageInfoRes?.data && imageInfoRes.data.data !== undefined)
                ? imageInfoRes.data.data
                : imageInfoRes.data
            if (infoPayload?.exists && infoPayload?.fileId) {
                const imgRes = await api.get(`/files/${infoPayload.fileId}/preview`, { responseType: 'blob' })
                const blob = new Blob([imgRes.data], { type: imgRes.headers?.['content-type'] || 'image/png' })
                setSectionBrowseImageUrl(URL.createObjectURL(blob))
                setSectionBrowseImageName(infoPayload?.imageName || '')
            }
        } catch (err) {
            const message = err?.response?.data?.message || '불러오기에 실패했습니다'
            show(message, 'error')
        } finally {
            setSectionBrowseLoading(false)
        }
    }

    const openSectionBrowse = (type) => {
        const items = type === 'LIFE' ? lifeItems : fireItems
        const list = buildInsurerBrowseList(items)
        if (list.length === 0) {
            show('해당 구분에 소식지가 없습니다', 'info')
            return
        }
        setSectionBrowseType(type)
        setSectionBrowseList(list)
        setSectionBrowseIndex(0)
        setSectionBrowseOpen(true)
        loadSectionBrowseAtIndex(list, 0)
    }

    const closeSectionBrowse = () => {
        setSectionBrowseOpen(false)
        setSectionBrowseType(null)
        setSectionBrowseList([])
        setSectionBrowseIndex(0)
        setSectionBrowseSummaryText('')
        setSectionBrowseImageName('')
        revokeSectionBrowseImage()
    }

    const goSectionBrowse = (delta) => {
        const next = sectionBrowseIndex + delta
        if (next < 0 || next >= sectionBrowseList.length) return
        setSectionBrowseIndex(next)
        loadSectionBrowseAtIndex(sectionBrowseList, next)
    }

    // 요약 텍스트를 섹션별로 파싱
    const parseSummarySections = (summaryText) => {
        if (!summaryText) return { newProducts: '', updates: '', promotion: '' }

        const sections = {
            newProducts: '',
            updates: '',
            promotion: ''
        }

        const normalizeHeader = (line) => (
            line
                .trim()
                .replace(/^[#>\-\s]+/, '')
                .replace(/\*\*/g, '')
                .replace(/[：:]/g, '')
                .replace(/\//g, '')
                .replace(/\s+/g, '')
        )

        const detectSection = (line) => {
            const h = normalizeHeader(line)
            if (h.startsWith('신규출시상품') || h.startsWith('신규상품')) return 'newProducts'
            if (h.startsWith('기존상품변경업데이트') || h.startsWith('기존상품변경') || h.startsWith('기존상품업데이트')) return 'updates'
            if (h.startsWith('홍보방향성') || h.startsWith('홍보방향')) return 'promotion'
            return null
        }

        const lines = summaryText.split('\n')
        let currentSection = null
        const bucket = {
            newProducts: [],
            updates: [],
            promotion: []
        }

        for (const rawLine of lines) {
            const line = rawLine.trim()
            if (!line) {
                if (currentSection) bucket[currentSection].push('')
                continue
            }

            const detected = detectSection(line)
            if (detected) {
                currentSection = detected
                continue
            }

            if (currentSection) {
                bucket[currentSection].push(rawLine)
            }
        }

        sections.newProducts = bucket.newProducts.join('\n').trim()
        sections.updates = bucket.updates.join('\n').trim()
        sections.promotion = bucket.promotion.join('\n').trim()

        return sections
    }

    // **굵게** 마크다운을 실제 굵게 렌더링
    const renderSummaryContent = (text) => {
        if (!text) return null
        return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={idx}>{part.slice(2, -2)}</strong>
            }
            return <React.Fragment key={idx}>{part}</React.Fragment>
        })
    }

    // 연도/월 옵션 생성 (2026년 1월부터)
    const years = Array.from({ length: currentYear - MIN_YEAR + 1 }, (_, i) => MIN_YEAR + i)
    
    // 월 옵션: 2026년이면 1월부터, 그 외에는 전체 월
    const getAvailableMonths = () => {
        if (year === MIN_YEAR) {
            return Array.from({ length: 12 - MIN_MONTH + 1 }, (_, i) => MIN_MONTH + i)
        }
        return Array.from({ length: 12 }, (_, i) => i + 1)
    }
    const months = getAvailableMonths()
    
    // 연도 변경 시 월 유효성 검사
    React.useEffect(() => {
        if (year === MIN_YEAR && month < MIN_MONTH) {
            setMonth(MIN_MONTH)
        }
    }, [year, month])

    React.useEffect(() => {
        return () => {
            try { if (imageModalUrl) URL.revokeObjectURL(imageModalUrl) } catch { }
        }
    }, [imageModalUrl])

    const renderTable = (items, type) => {
        if (items.length === 0) {
            return (
                <div className="p-8 text-center text-slate-500">
                    해당 월의 소식지가 없습니다
                </div>
            )
        }

        // 보험사별로 그룹화
        const groupedByInsurer = items.reduce((acc, item) => {
            const insurerKey = item.insurerName || '매핑 안됨'
            if (!acc[insurerKey]) {
                acc[insurerKey] = []
            }
            acc[insurerKey].push(item)
            return acc
        }, {})

        const insurerGroups = Object.entries(groupedByInsurer)

        return (
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                    <tr>
                        <th className="px-4 py-2 text-left">보험사</th>
                        {isOperator && (
                            <>
                                <th className="px-4 py-2 text-left">파일명</th>
                                <th className="px-4 py-2 text-left">파일 크기</th>
                                <th className="px-4 py-2 text-left">업로드일</th>
                            </>
                        )}
                        <th className="px-4 py-2 text-right">관리</th>
                    </tr>
                </thead>
                <tbody>
                    {insurerGroups.map(([insurerName, groupItems], groupIndex) => (
                        <React.Fragment key={insurerName}>
                            {groupItems.map((item, itemIndex) => (
                                <React.Fragment key={item.id}>
                                    {itemIndex === 0 && groupIndex > 0 && (
                                        <tr>
                                            <td colSpan={isOperator ? 5 : 2} className="px-4 py-1 border-t border-slate-200"></td>
                                        </tr>
                                    )}
                                    <tr className="hover:bg-slate-50">
                                        <td className="px-4 py-2">
                                            {item.insurerName ? (
                                                <span className="font-medium text-slate-900">{item.insurerName}</span>
                                            ) : (
                                                <span className="text-slate-400 italic">매핑 안됨</span>
                                            )}
                                        </td>
                                        {isOperator && (
                                            <>
                                                <td className="px-4 py-2 text-slate-600">{item.filename}</td>
                                                <td className="px-4 py-2 text-slate-600">
                                                    {item.fileSize ? `${(item.fileSize / 1024).toFixed(1)} KB` : '-'}
                                                </td>
                                                <td className="px-4 py-2 text-slate-600">
                                                    {item.uploadedAt ? formatDateTimeMinute(item.uploadedAt) : '-'}
                                                </td>
                                            </>
                                        )}
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {(() => {
                                                    return (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePreview(item.id)}
                                                                className="text-xs text-brand-sky hover:underline"
                                                            >
                                                                미리보기
                                                            </button>
                                                            <span className="text-slate-300">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDownload(item.id, item.filename)}
                                                                className="text-xs text-brand-sky hover:underline"
                                                            >
                                                                다운로드
                                                            </button>
                                                            <span className="text-slate-300">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewSummary(item.id)}
                                                                className="text-xs text-purple-600 hover:underline"
                                                                title="요약 보기"
                                                            >
                                                                요약
                                                            </button>
                                                            <span className="text-slate-300">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewImage(item.id)}
                                                                className="text-xs text-emerald-600 hover:underline"
                                                            >
                                                                이미지
                                                            </button>
                                                        </>
                                                    )
                                                })()}
                                                {isOperator && (
                                                    <>
                                                        <span className="text-slate-300">|</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAiSummary(item)}
                                                            className="text-xs text-purple-600 hover:underline"
                                                            disabled={summaryLoading}
                                                        >
                                                            AI 정리
                                                        </button>
                                                        <span className="text-slate-300">|</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleGenerateImage(item.id)}
                                                            disabled={imageGenLoadingId === item.id}
                                                            className="text-xs text-emerald-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            AI 이미지 생성
                                                        </button>
                                                        <span className="text-slate-300">|</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(item.id)}
                                                            className="text-xs text-red-600 hover:underline"
                                                        >
                                                            삭제
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        )
    }

    const filterHeader = (
        <div className="p-4 border-b bg-white">
            {isOperator && (
                <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
                        <span className="text-sm text-slate-600">이미지 엔진</span>
                        <select
                            value={newsletterImageProvider}
                            onChange={(e) => {
                                const v = e.target.value
                                setNewsletterImageProvider(v)
                                if (v === 'openai') {
                                    setSelectedImageModel('gpt-image-2')
                                    setOpenaiImageQuality('medium')
                                    setOpenaiImageSize('1024x1024')
                                } else {
                                    setSelectedImageModel('gemini-3.1-flash-image')
                                }
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        >
                            <option value="gemini">Gemini</option>
                            <option value="openai">GPT Image 2</option>
                        </select>
                        {newsletterImageProvider === 'openai' && (
                            <>
                                <span className="text-sm text-slate-600">품질</span>
                                <select
                                    value={openaiImageQuality}
                                    onChange={(e) => setOpenaiImageQuality(e.target.value)}
                                    className="rounded-lg border border-slate-300 px-2 py-2 text-sm"
                                >
                                    {openAiQualityOptions.map((q) => (
                                        <option key={q} value={q}>{q}</option>
                                    ))}
                                </select>
                                <span className="text-sm text-slate-600">크기</span>
                                <select
                                    value={openaiImageSize}
                                    onChange={(e) => setOpenaiImageSize(e.target.value)}
                                    className="rounded-lg border border-slate-300 px-2 py-2 text-sm max-w-[140px]"
                                >
                                    {openAiSizeOptions.map((sz) => (
                                        <option key={sz} value={sz}>{sz}</option>
                                    ))}
                                </select>
                            </>
                        )}
                        <span className="text-sm text-slate-600">AI 이미지 모델</span>
                        <select
                            value={selectedImageModel}
                            onChange={(e) => setSelectedImageModel(e.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        >
                            {imageModelSelectOptions.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        <select
                            value={imageProcessType}
                            onChange={(e) => setImageProcessType(e.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                            <option value="플라이어">플라이어</option>
                            <option value="보상">보상</option>
                        </select>
                        <select value={imageStyle} onChange={(e) => setImageStyle(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                            <option value="충격통계">충격통계</option>
                            <option value="공감질문">공감질문</option>
                            <option value="체크리스트">체크리스트</option>
                            <option value="안부터치">안부터치</option>
                        </select>
                        <select value={imageAudience} onChange={(e) => setImageAudience(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                            <option value="2030">2030</option>
                            <option value="3040">3040</option>
                            <option value="5060">5060</option>
                            <option value="전체">전체</option>
                        </select>
                        <select value={imageCardCount} onChange={(e) => setImageCardCount(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                            <option value="1">1장</option>
                            <option value="2">2장</option>
                            <option value="3">3장</option>
                            <option value="4">4장</option>
                        </select>
                        <select value={imageRatio} onChange={(e) => setImageRatio(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                            <option value="1:1">1:1</option>
                            <option value="4:5">4:5</option>
                            <option value="9:16">9:16</option>
                            <option value="16:9">16:9</option>
                        </select>
                        <input
                            value={imageTone}
                            onChange={(e) => setImageTone(e.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm min-w-[220px]"
                            placeholder="톤 (예: 따뜻하고 전문적)"
                        />
                </div>
            )}
            <div className="flex flex-row items-center gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-slate-700">연도:</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    >
                        {years.map(y => (
                            <option key={y} value={y}>{y}년</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-slate-700">월:</label>
                    <select
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    >
                        {months.map(m => (
                            <option key={m} value={m}>{m}월</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    )

    return (
        <div className="flex flex-col h-full">
            <PageHeader
                title="보험사 소식지"
                titleAccessory={<><AiFeatureBadge /><MenuGuideButton slug="insurer-newsletters" title="업무지원 가이드" /></>}
                className="mb-5"
            />
            {filterHeader}
            <div className="flex-1 overflow-auto">
                {loading && (
                    <div className="p-8 text-center text-slate-500">로딩 중...</div>
                )}
                {error && (
                    <div className="p-8 text-center text-red-500">
                        {error}
                        <button
                            onClick={() => {
                                lifeQuery.refetch()
                                fireQuery.refetch()
                            }}
                            className="ml-2 text-brand-sky hover:underline"
                        >
                            다시 시도
                        </button>
                    </div>
                )}
                {!loading && !error && (
                    <>
                    {/* ── 이달의 이슈 브리프 ── */}
                    <div className="mx-4 mt-4 border border-indigo-200 rounded-lg bg-white">
                        <div
                            className="p-4 border-b bg-indigo-50 flex flex-wrap items-center justify-between gap-3 cursor-pointer select-none"
                            onClick={() => setBriefExpanded(v => !v)}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-lg" aria-hidden>{briefExpanded ? '▾' : '▸'}</span>
                                <h2 className="text-lg font-semibold text-indigo-900">이달의 이슈</h2>
                                {briefData?.status === 'COMPLETED' && (
                                    <span className="text-xs rounded-full bg-green-100 text-green-800 px-2 py-0.5">생성 완료</span>
                                )}
                                {briefData?.status === 'IN_PROGRESS' && (
                                    <span className="text-xs rounded-full bg-yellow-100 text-yellow-800 px-2 py-0.5">생성 중...</span>
                                )}
                                {briefData?.status === 'FAILED' && (
                                    <span className="text-xs rounded-full bg-red-100 text-red-800 px-2 py-0.5">실패</span>
                                )}
                                {briefData?.published && (
                                    <span className="text-xs rounded-full bg-blue-100 text-blue-800 px-2 py-0.5">공개</span>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2" onClick={e => e.stopPropagation()}>
                                {briefData?.status === 'COMPLETED' && (() => {
                                    const lifeN = Array.isArray(briefData?.payload?.lifeBrief?.slides) ? briefData.payload.lifeBrief.slides.length : 0
                                    const fireN = Array.isArray(briefData?.payload?.fireBrief?.slides) ? briefData.payload.fireBrief.slides.length : 0
                                    return (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => openCardNewsModal('life')}
                                                disabled={lifeN === 0}
                                                className="text-xs rounded-lg border border-indigo-400 bg-white px-3 py-1 text-indigo-700 hover:bg-indigo-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                생보 카드뉴스
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openCardNewsModal('fire')}
                                                disabled={fireN === 0}
                                                className="text-xs rounded-lg border border-violet-400 bg-white px-3 py-1 text-violet-800 hover:bg-violet-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                손보 카드뉴스
                                            </button>
                                        </>
                                    )
                                })()}
                                {isOperator && briefData?.id && (
                                    <button
                                        type="button"
                                        onClick={handleToggleBriefPublish}
                                        className="text-xs rounded-lg border border-slate-300 bg-white px-3 py-1 text-slate-700 hover:bg-slate-50"
                                    >
                                        {briefData.published ? '비공개 전환' : '공개 전환'}
                                    </button>
                                )}
                                {isOperator && (
                                    <button
                                        type="button"
                                        onClick={handleGenerateBrief}
                                        disabled={briefGenerating || briefData?.status === 'IN_PROGRESS'}
                                        className="text-xs rounded-lg bg-indigo-600 px-3 py-1 text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {briefGenerating || briefData?.status === 'IN_PROGRESS' ? '생성 중...' : briefData?.status === 'COMPLETED' ? '재생성' : '이슈 생성'}
                                    </button>
                                )}
                            </div>
                        </div>
                        {briefExpanded && (
                            <div className="p-4">
                                {!briefData || !briefData.payload ? (
                                    <div className="text-center py-8 text-slate-500">
                                        {briefData?.status === 'IN_PROGRESS' ? (
                                            <div>
                                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mb-2"></div>
                                                <p className="text-sm">이달의 이슈를 생성하고 있습니다...</p>
                                            </div>
                                        ) : briefData?.status === 'FAILED' ? (
                                            <p className="text-sm text-red-600">생성에 실패했습니다: {briefData.errorMessage || '알 수 없는 오류'}</p>
                                        ) : (
                                            <p className="text-sm">아직 이달의 이슈가 생성되지 않았습니다. {isOperator ? '「이슈 생성」 버튼을 눌러주세요.' : ''}</p>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        {!isOperator ? (
                                            <div className="space-y-6">
                                                {['life', 'fire'].map((tab) => {
                                                    const brief = tab === 'life' ? briefData.payload.lifeBrief : briefData.payload.fireBrief
                                                    if (!brief) return null
                                                    const summary = brief.issuesSummary || ''
                                                    if (!summary) return null
                                                    return (
                                                        <div key={tab} className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4">
                                                            <h3 className="text-sm font-semibold text-indigo-900 mb-2">
                                                                {tab === 'life' ? '생명보험' : '손해보험'} 핵심 요약
                                                            </h3>
                                                            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{summary}</p>
                                                        </div>
                                                    )
                                                })}
                                                {briefData.generatedAt && (
                                                    <p className="text-xs text-slate-400 text-right">
                                                        생성: {formatApiDateTimeDisplay(briefData.generatedAtDisplay, briefData.generatedAt)}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex gap-2 mb-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => setBriefBriefTab('life')}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                                            briefBriefTab === 'life'
                                                                ? 'bg-indigo-600 text-white'
                                                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                        }`}
                                                    >
                                                        생보 이슈 (편집·이미지)
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBriefBriefTab('fire')}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                                            briefBriefTab === 'fire'
                                                                ? 'bg-indigo-600 text-white'
                                                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                        }`}
                                                    >
                                                        손보 이슈 (편집·이미지)
                                                    </button>
                                                </div>
                                                {(() => {
                                                    const brief = briefBriefTab === 'life' ? briefData.payload.lifeBrief : briefData.payload.fireBrief
                                                    if (!brief) return <p className="text-sm text-slate-500">데이터가 없습니다.</p>
                                                    const summary = brief.issuesSummary || ''
                                                    const slides = Array.isArray(brief.slides) ? brief.slides : []
                                                    return (
                                                        <div className="space-y-4">
                                                            {summary && (
                                                                <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4">
                                                                    <h3 className="text-sm font-semibold text-indigo-900 mb-1">핵심 요약</h3>
                                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{summary}</p>
                                                                </div>
                                                            )}
                                                            <p className="text-xs text-slate-500">
                                                                생보·손보는 각각 별도 LLM 결과·슬라이드·이미지로 관리됩니다. 카드 파일명에 life_/fire_ 접두사가 붙습니다.
                                                            </p>
                                                            {slides.length > 0 && (
                                                                <div>
                                                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                                        <h3 className="text-sm font-semibold text-slate-900">카드뉴스 슬라이드 (운영)</h3>
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <span className="text-xs text-slate-600">엔진</span>
                                                                            <select
                                                                                value={newsletterImageProvider}
                                                                                onChange={(e) => {
                                                                                    const v = e.target.value
                                                                                    setNewsletterImageProvider(v)
                                                                                    if (v === 'openai') {
                                                                                        setSelectedImageModel('gpt-image-2')
                                                                                        setOpenaiImageQuality('medium')
                                                                                        setOpenaiImageSize('1024x1024')
                                                                                    } else {
                                                                                        setSelectedImageModel('gemini-3.1-flash-image')
                                                                                    }
                                                                                }}
                                                                                className="rounded-lg border border-slate-300 px-2 py-1 text-xs max-w-[120px]"
                                                                            >
                                                                                <option value="gemini">Gemini</option>
                                                                                <option value="openai">GPT Img 2</option>
                                                                            </select>
                                                                            {newsletterImageProvider === 'openai' && (
                                                                                <>
                                                                                    <select
                                                                                        value={openaiImageQuality}
                                                                                        onChange={(e) => setOpenaiImageQuality(e.target.value)}
                                                                                        className="rounded-lg border border-slate-300 px-1 py-1 text-xs max-w-[88px]"
                                                                                        title="quality"
                                                                                    >
                                                                                        {openAiQualityOptions.map((q) => (
                                                                                            <option key={q} value={q}>{q}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                    <select
                                                                                        value={openaiImageSize}
                                                                                        onChange={(e) => setOpenaiImageSize(e.target.value)}
                                                                                        className="rounded-lg border border-slate-300 px-1 py-1 text-xs max-w-[100px]"
                                                                                        title="size"
                                                                                    >
                                                                                        {openAiSizeOptions.map((sz) => (
                                                                                            <option key={sz} value={sz}>{sz}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                </>
                                                                            )}
                                                                            <span className="text-xs text-slate-600">모델</span>
                                                                            <select
                                                                                value={selectedImageModel}
                                                                                onChange={(e) => setSelectedImageModel(e.target.value)}
                                                                                className="rounded-lg border border-slate-300 px-2 py-1 text-xs focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20 max-w-[200px]"
                                                                            >
                                                                                {imageModelSelectOptions.map((m) => (
                                                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                                                ))}
                                                                            </select>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleBatchGenerateCardImages(briefBriefTab)}
                                                                                disabled={briefBatchImageLoading}
                                                                                className="text-xs rounded-lg bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                                                                            >
                                                                                {briefBatchImageLoading ? '일괄 생성 중...' : '이미지 일괄 생성'}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => cardImagesQuery.refetch()}
                                                                                className="text-xs rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-600 hover:bg-slate-50"
                                                                            >
                                                                                새로고침
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                        {slides.map((slide, idx) => {
                                                                            const storageSid = monthlyBriefSlideStorageId(briefBriefTab, slide, idx)
                                                                            const imgInfo = slideImageMap[storageSid]
                                                                            return (
                                                                                <div key={storageSid} className="border border-slate-200 rounded-lg p-3 bg-white hover:shadow-sm transition-shadow">
                                                                                    {imgInfo?.fileId && (
                                                                                        <SlideImagePreview fileId={imgInfo.fileId} />
                                                                                    )}
                                                                                    <div className="flex items-start justify-between gap-2 mb-1">
                                                                                        <span className="text-xs font-medium text-indigo-600">#{idx + 1}</span>
                                                                                        {slide.tags && Array.isArray(slide.tags) && slide.tags.length > 0 && (
                                                                                            <div className="flex gap-1 flex-wrap">
                                                                                                {slide.tags.map((tag, ti) => (
                                                                                                    <span key={ti} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag}</span>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <h4 className="text-sm font-semibold text-slate-900 mb-1">{slide.title}</h4>
                                                                                    <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">{slide.body}</p>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => handleGenerateCardImage(storageSid, slide.title, slide.body,
                                                                                            briefBriefTab === 'life' ? 'LIFE' : 'FIRE'
                                                                                        )}
                                                                                        disabled={briefCardImageLoading === storageSid}
                                                                                        className="mt-2 text-xs rounded bg-emerald-600 px-2 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                                                                                    >
                                                                                        {briefCardImageLoading === storageSid ? '생성 중...' : imgInfo?.fileId ? '이미지 재생성' : '이미지 생성'}
                                                                                    </button>
                                                                                </div>
                                                                            )
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {briefData.generatedAt && (
                                                                <p className="text-xs text-slate-400 text-right">
                                                                    생성: {formatApiDateTimeDisplay(briefData.generatedAtDisplay, briefData.generatedAt)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )
                                                })()}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
                        {/* 생명보험 섹션 */}
                        <div className="border border-slate-200 rounded-lg bg-white">
                            <div className="p-4 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                                <h2 className="text-lg font-semibold text-slate-900 shrink-0">생명보험</h2>
                                <div className="flex flex-wrap items-center gap-2 justify-end ml-auto">
                                    <button
                                        type="button"
                                        onClick={() => openSectionBrowse('LIFE')}
                                        disabled={lifeItems.length === 0}
                                        className="text-xs rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        전체 요약/이미지 보기
                                    </button>
                                {isOperator && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleSync('LIFE')}
                                            disabled={syncing}
                                            className="text-xs rounded-lg border border-slate-300 bg-white px-3 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                        >
                                            {syncing ? '동기화 중...' : '동기화'}
                                        </button>
                                        <label className="text-xs rounded-lg bg-brand-sky px-3 py-1 text-white hover:bg-brand-sky/90 cursor-pointer">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept=".pdf"
                                                onChange={(e) => handleUpload(e, 'LIFE')}
                                                disabled={uploading}
                                                className="hidden"
                                            />
                                            {uploading ? '업로드 중...' : '일괄 업로드'}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleBatchSummary('LIFE')}
                                            disabled={batchSummarizing}
                                            className="text-xs rounded-lg bg-purple-600 px-3 py-1 text-white hover:bg-purple-700 disabled:opacity-50"
                                        >
                                            {batchSummarizing ? 'AI 정리 중...' : 'AI 정리'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleBatchGenerateImage('LIFE')}
                                            disabled={batchGeneratingImage}
                                            className="text-xs rounded-lg bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                                        >
                                            {batchGeneratingImage ? 'AI 이미지 생성 중...' : 'AI 이미지 생성'}
                                        </button>
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                {renderTable(lifeItems, 'LIFE')}
                            </div>
                        </div>

                        {/* 손해보험 섹션 */}
                        <div className="border border-slate-200 rounded-lg bg-white">
                            <div className="p-4 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                                <h2 className="text-lg font-semibold text-slate-900 shrink-0">손해보험</h2>
                                <div className="flex flex-wrap items-center gap-2 justify-end ml-auto">
                                    <button
                                        type="button"
                                        onClick={() => openSectionBrowse('FIRE')}
                                        disabled={fireItems.length === 0}
                                        className="text-xs rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        전체 요약/이미지 보기
                                    </button>
                                {isOperator && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleSync('FIRE')}
                                            disabled={syncing}
                                            className="text-xs rounded-lg border border-slate-300 bg-white px-3 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                        >
                                            {syncing ? '동기화 중...' : '동기화'}
                                        </button>
                                        <label className="text-xs rounded-lg bg-brand-sky px-3 py-1 text-white hover:bg-brand-sky/90 cursor-pointer">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept=".pdf"
                                                onChange={(e) => handleUpload(e, 'FIRE')}
                                                disabled={uploading}
                                                className="hidden"
                                            />
                                            {uploading ? '업로드 중...' : '일괄 업로드'}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleBatchSummary('FIRE')}
                                            disabled={batchSummarizing}
                                            className="text-xs rounded-lg bg-purple-600 px-3 py-1 text-white hover:bg-purple-700 disabled:opacity-50"
                                        >
                                            {batchSummarizing ? 'AI 정리 중...' : 'AI 정리'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleBatchGenerateImage('FIRE')}
                                            disabled={batchGeneratingImage}
                                            className="text-xs rounded-lg bg-emerald-600 px-3 py-1 text-white hover:bg-emerald-700 disabled:opacity-50"
                                        >
                                            {batchGeneratingImage ? 'AI 이미지 생성 중...' : 'AI 이미지 생성'}
                                        </button>
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                {renderTable(fireItems, 'FIRE')}
                            </div>
                        </div>
                    </div>
                    </>
                )}
            </div>
            {/* 요약 모달 */}
            {summaryModalOpen && (() => {
                const operatorRawText = summaryViewTab === 'exposure'
                    ? (summaryData?.effectiveSummary || summaryData?.finalArbitrated || summaryData?.['gpt-4o-mini'] || '')
                    : summaryViewTab === 'aiFinal'
                        ? (summaryData?.finalArbitrated || summaryData?.['gpt-4o-mini'] || '')
                        : summaryViewTab === 'gpt'
                            ? (summaryData?.['gpt-4o-mini'] || '')
                            : (summaryData?.gemini || '')
                const exposureRaw = summaryData?.effectiveSummary || summaryData?.finalArbitrated || summaryData?.['gpt-4o-mini'] || ''
                const summaryText = isOperator
                    ? operatorRawText
                    : (insurerNewsletterExposureTextForUser(exposureRaw, false).trim() || INSURER_NEWSLETTER_USER_PREPARING_SUMMARY)
                const endUserPreparingOnly = !isOperator && summaryText === INSURER_NEWSLETTER_USER_PREPARING_SUMMARY
                const sections = parseSummarySections(summaryText)
                const insurerName = summaryData?.insurerName || '알 수 없음'
                const summaryYear = summaryData?.year || year
                const summaryMonth = summaryData?.month || month
                
                return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="px-6 py-4 border-b flex items-center justify-between">
                                <h2 className="text-xl font-bold">
                                    {insurerName} {summaryYear} - {summaryMonth} 소식지 요약
                                    <AiGeneratedBadge className="ml-2 align-middle" />
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSummaryModalOpen(false)
                                        setSummaryData(null)
                                        setSummaryViewTab('exposure')
                                        setManualDraft('')
                                        setCurrentNewsletterId(null)
                                    }}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="flex-1 overflow-auto p-6">
                                {summaryLoading ? (
                                    <div className="text-center py-12">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-sky"></div>
                                        <p className="mt-4 text-slate-600">요약을 불러오는 중...</p>
                                    </div>
                                ) : summaryData ? (
                                    <div className="space-y-6">
                                        {isOperator && (
                                            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/80 space-y-2">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <h3 className="font-semibold text-amber-900 text-sm">직접 요약 (운영자)</h3>
                                                    {summaryData?.summaryManualAt && (
                                                        <span className="text-xs text-amber-800">
                                                            저장: {formatApiDateTimeDisplay(summaryData?.summaryManualAtDisplay, summaryData?.summaryManualAt)}
                                                            {summaryData?.summaryManualUpdatedByEmail ? ` · ${summaryData.summaryManualUpdatedByEmail}` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-amber-900/80">
                                                    OCR/AI로 복구가 어려울 때 발송·노출에 쓰일 요약을 직접 입력합니다. 저장 시 다른 요약보다 우선합니다.
                                                </p>
                                                <textarea
                                                    value={manualDraft}
                                                    onChange={(e) => setManualDraft(e.target.value)}
                                                    rows={6}
                                                    className="w-full rounded border border-amber-300 px-3 py-2 text-sm text-slate-900 bg-white"
                                                    placeholder="섹션 형식(신규 출시 상품 등)을 맞추면 아래 카드에도 반영됩니다."
                                                />
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        disabled={manualSaving || !currentNewsletterId}
                                                        onClick={() => handleSaveManualSummary()}
                                                        className="text-xs rounded-lg bg-amber-700 px-3 py-1.5 text-white hover:bg-amber-800 disabled:opacity-50"
                                                    >
                                                        {manualSaving ? '저장 중...' : '직접 요약 저장'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={manualSaving || !summaryData?.summaryManual}
                                                        onClick={() => handleClearManualSummary()}
                                                        className="text-xs rounded-lg border border-amber-400 px-3 py-1.5 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                                    >
                                                        직접 요약 삭제
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {/* <div className="flex flex-wrap gap-2 items-center text-xs">
                                            <span className="text-slate-500 mr-1">보기:</span>
                                            {[
                                                { id: 'exposure', label: '노출(최종)' },
                                                { id: 'aiFinal', label: 'AI 최종(중재)' },
                                                { id: 'gpt', label: 'GPT-4o-mini' },
                                                { id: 'gemini', label: 'Gemini' },
                                            ].map((t) => (
                                                <button
                                                    key={t.id}
                                                    type="button"
                                                    onClick={() => setSummaryViewTab(t.id)}
                                                    className={`rounded px-2 py-1 border ${summaryViewTab === t.id ? 'bg-purple-100 border-purple-300 text-purple-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    {t.label}
                                                </button>
                                            ))}
                                            {summaryData?.textSource && (
                                                <span className="text-slate-400 ml-auto">
                                                    원문: {summaryData.textSource === 'OCR_TEXTRACT' ? 'OCR(Textract)' : 'PDF 텍스트'}
                                                </span>
                                            )}
                                        </div> */}
                                        {endUserPreparingOnly ? (
                                            <div className="text-center py-12 text-slate-600">
                                                {INSURER_NEWSLETTER_USER_PREPARING_SUMMARY}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="border rounded-lg p-4">
                                                    <h3 className="font-semibold text-lg mb-3 text-slate-900">신규 출시 상품</h3>
                                                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sections.newProducts ? renderSummaryContent(sections.newProducts) : '신규 출시 상품 정보가 없습니다.'}
                                                    </div>
                                                </div>

                                                <div className="border rounded-lg p-4">
                                                    <h3 className="font-semibold text-lg mb-3 text-slate-900">기존상품 변경/업데이트</h3>
                                                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sections.updates ? renderSummaryContent(sections.updates) : '기존상품 변경/업데이트 정보가 없습니다.'}
                                                    </div>
                                                </div>

                                                <div className="border rounded-lg p-4">
                                                    <h3 className="font-semibold text-lg mb-3 text-slate-900">홍보 방향성</h3>
                                                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sections.promotion ? renderSummaryContent(sections.promotion) : '홍보 방향성 정보가 없습니다.'}
                                                    </div>
                                                </div>

                                                {/* 섹션 파싱이 안된 경우 전체 텍스트 표시 */}
                                                {!sections.newProducts && !sections.updates && !sections.promotion && summaryText && (
                                                    <div className="border rounded-lg p-4">
                                                        <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                            {summaryText}
                                                        </div>
                                                    </div>
                                                )}

                                                {!summaryText && (
                                                    <div className="text-center py-12 text-slate-500">
                                                        요약 데이터가 없습니다.
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-slate-500">
                                        요약 데이터가 없습니다.
                                    </div>
                                )}
                            </div>
                            <div className="px-6 py-4 border-t flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSummaryModalOpen(false)
                                        setSummaryData(null)
                                        setSummaryViewTab('exposure')
                                        setManualDraft('')
                                        setCurrentNewsletterId(null)
                                    }}
                                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
            {imageModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <h2 className="text-lg font-bold">
                                발송용 이미지 {imageModalName ? `- ${imageModalName}` : ''}
                                <AiGeneratedBadge className="ml-2 align-middle" />
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setImageModalOpen(false)
                                    setImageModalLoading(false)
                                    setImageModalName('')
                                    setImageModalUrl((prev) => {
                                        try { if (prev) URL.revokeObjectURL(prev) } catch { }
                                        return ''
                                    })
                                }}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4 bg-slate-50">
                            {imageModalLoading ? (
                                <div className="text-center py-12 text-slate-500">이미지를 불러오는 중...</div>
                            ) : imageModalUrl ? (
                                <img src={imageModalUrl} alt="발송용 이미지" className="w-full max-h-[75vh] object-contain rounded border bg-white" />
                            ) : (
                                <div className="text-center py-12 text-slate-500">표시할 이미지가 없습니다.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {sectionBrowseOpen && (() => {
                const cur = sectionBrowseList[sectionBrowseIndex]
                const sectionLabel = sectionBrowseType === 'LIFE' ? '생명보험' : '손해보험'
                const titleInsurer = cur?.insurerName || '알 수 없음'
                const titleYm = cur ? `${cur.year}년 ${cur.month}월` : ''
                const sbSummary = sectionBrowseSummaryText || ''
                const sbSections = parseSummarySections(sbSummary)
                return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 sm:p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
                            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-slate-50 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs text-slate-500 mb-0.5">{sectionLabel}</p>
                                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 truncate">
                                        {titleInsurer}
                                        {titleYm ? <span className="font-semibold text-slate-600"> · {titleYm}</span> : null}
                                        <AiGeneratedBadge className="ml-2 align-middle" />
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {sectionBrowseList.length > 0
                                            ? `${sectionBrowseIndex + 1} / ${sectionBrowseList.length}`
                                            : ''}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeSectionBrowse}
                                    className="shrink-0 text-slate-400 hover:text-slate-600 text-xl leading-none"
                                    aria-label="닫기"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="flex-1 min-h-0 overflow-hidden flex flex-col lg:flex-row">
                                <div className="lg:w-1/2 border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col min-h-[200px] max-h-[50vh] lg:max-h-none">
                                    <div className="px-3 py-2 bg-purple-50/80 border-b border-slate-100 text-xs font-semibold text-purple-900">
                                        요약
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-4">
                                        {sectionBrowseLoading ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-sky" />
                                                <p className="mt-3 text-sm">불러오는 중...</p>
                                            </div>
                                        ) : sbSummary ? (
                                            <div className="space-y-4 text-sm">
                                                <div className="border rounded-lg p-3 bg-white">
                                                    <h3 className="font-semibold mb-2 text-slate-900">신규 출시 상품</h3>
                                                    <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sbSections.newProducts
                                                            ? renderSummaryContent(sbSections.newProducts)
                                                            : '신규 출시 상품 정보가 없습니다.'}
                                                    </div>
                                                </div>
                                                <div className="border rounded-lg p-3 bg-white">
                                                    <h3 className="font-semibold mb-2 text-slate-900">기존상품 변경/업데이트</h3>
                                                    <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sbSections.updates
                                                            ? renderSummaryContent(sbSections.updates)
                                                            : '기존상품 변경/업데이트 정보가 없습니다.'}
                                                    </div>
                                                </div>
                                                <div className="border rounded-lg p-3 bg-white">
                                                    <h3 className="font-semibold mb-2 text-slate-900">홍보 방향성</h3>
                                                    <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                                                        {sbSections.promotion
                                                            ? renderSummaryContent(sbSections.promotion)
                                                            : '홍보 방향성 정보가 없습니다.'}
                                                    </div>
                                                </div>
                                                {!sbSections.newProducts && !sbSections.updates && !sbSections.promotion && (
                                                    <div className="border rounded-lg p-3 bg-white whitespace-pre-wrap text-slate-700 leading-relaxed">
                                                        {sbSummary}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-slate-500 text-center py-8">
                                                {isOperator ? '저장된 요약이 없습니다.' : INSURER_NEWSLETTER_USER_PREPARING_SUMMARY}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="lg:w-1/2 flex flex-col min-h-[200px] max-h-[50vh] lg:max-h-none">
                                    <div className="px-3 py-2 bg-emerald-50/80 border-b border-slate-100 text-xs font-semibold text-emerald-900">
                                        발송용 이미지
                                        {sectionBrowseImageName ? (
                                            <span className="font-normal text-emerald-700"> · {sectionBrowseImageName}</span>
                                        ) : null}
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-3 bg-slate-50 flex items-center justify-center min-h-[180px]">
                                        {sectionBrowseLoading ? (
                                            <div className="text-sm text-slate-500">불러오는 중...</div>
                                        ) : sectionBrowseImageUrl ? (
                                            <img
                                                src={sectionBrowseImageUrl}
                                                alt="발송용 이미지"
                                                className="max-w-full max-h-[min(60vh,720px)] object-contain rounded border border-slate-200 bg-white shadow-sm"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-500 text-center px-4">생성된 발송용 이미지가 없습니다.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="px-4 sm:px-6 py-3 border-t bg-white flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => goSectionBrowse(-1)}
                                    disabled={sectionBrowseIndex <= 0 || sectionBrowseLoading}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <span aria-hidden>‹</span> 이전 보험사
                                </button>
                                <button
                                    type="button"
                                    onClick={closeSectionBrowse}
                                    className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700 hover:bg-slate-200"
                                >
                                    닫기
                                </button>
                                <button
                                    type="button"
                                    onClick={() => goSectionBrowse(1)}
                                    disabled={
                                        sectionBrowseIndex >= sectionBrowseList.length - 1 || sectionBrowseLoading
                                    }
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    다음 보험사 <span aria-hidden>›</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
            {/* 카드뉴스 캐러셀 모달 */}
            {cardNewsModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="px-5 py-3 border-b bg-indigo-50 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-indigo-900">
                                카드뉴스 · {year}년 {month}월 · {cardNewsKind === 'life' ? '생명보험' : '손해보험'}
                                <AiGeneratedBadge className="ml-2 align-middle" />
                            </h2>
                            <button type="button" onClick={closeCardNewsModal} className="text-slate-400 hover:text-slate-600 text-xl leading-none">✕</button>
                        </div>
                        <div className="flex-1 overflow-auto flex items-start justify-center bg-slate-50 min-h-[300px]">
                            {cardNewsLoading ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                                    <p className="mt-3 text-sm text-slate-500">이미지를 불러오는 중...</p>
                                </div>
                            ) : cardNewsImages.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center px-6 py-10">
                                    이 구간(생보/손보)에 생성된 카드뉴스 이미지가 없습니다. {isOperator ? '해당 탭에서 「이미지 일괄 생성」을 실행해 주세요. 구 이미지는 생보 카드뉴스에만 보일 수 있습니다.' : ''}
                                </p>
                            ) : (
                                <div className="w-full p-4">
                                    <div className="relative">
                                        {cardNewsImages[cardNewsIndex]?.blobUrl ? (
                                            <img
                                                src={cardNewsImages[cardNewsIndex].blobUrl}
                                                alt={`카드뉴스 ${cardNewsIndex + 1}`}
                                                className="w-full max-h-[55vh] object-contain rounded-lg border border-slate-200 bg-white shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-full h-48 flex items-center justify-center bg-slate-100 rounded-lg border border-slate-200">
                                                <p className="text-sm text-slate-400">이미지를 불러올 수 없습니다</p>
                                            </div>
                                        )}
                                    </div>
                                    {(() => {
                                        const cur = cardNewsImages[cardNewsIndex]
                                        const storageSid = cur ? parseStorageSlideIdFromMonthlyBriefImageName(cur.imageName) : null
                                        const meta = briefData?.payload && storageSid
                                            ? findSlideMetaForMonthlyBriefImage(briefData.payload, cardNewsKind, storageSid)
                                            : null
                                        if (!meta || (!meta.title && !meta.body)) return null
                                        return (
                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm">
                                                {meta.title ? (
                                                    <h3 className="text-base font-bold text-slate-900 mb-2">{meta.title}</h3>
                                                ) : null}
                                                {meta.body ? (
                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{meta.body}</p>
                                                ) : null}
                                            </div>
                                        )
                                    })()}
                                </div>
                            )}
                        </div>
                        {cardNewsImages.length > 0 && (
                            <div className="px-5 py-3 border-t bg-white flex flex-wrap items-center justify-between gap-2">
                                <button
                                    type="button"
                                    onClick={() => setCardNewsIndex(i => Math.max(0, i - 1))}
                                    disabled={cardNewsIndex <= 0}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <span aria-hidden>‹</span> 이전
                                </button>
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    <span className="text-sm text-slate-600 font-medium tabular-nums">
                                        {cardNewsIndex + 1} / {cardNewsImages.length}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => void downloadCurrentCardNewsImage()}
                                        disabled={
                                            cardNewsDownloading || !cardNewsImages[cardNewsIndex]?.fileId
                                        }
                                        className="inline-flex items-center rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {cardNewsDownloading ? '받는 중…' : '이미지 다운로드'}
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCardNewsIndex(i => Math.min(cardNewsImages.length - 1, i + 1))}
                                    disabled={cardNewsIndex >= cardNewsImages.length - 1}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    다음 <span aria-hidden>›</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

/** 슬라이드별 이미지 썸네일 — lazy blob 로딩 */
function SlideImagePreview({ fileId }) {
    const [url, setUrl] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        setUrl(null)
        api.get(`/files/${fileId}/preview`, { responseType: 'blob' })
            .then(res => {
                if (cancelled) return
                setUrl(URL.createObjectURL(res.data))
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => {
            cancelled = true
            if (url) try { URL.revokeObjectURL(url) } catch {}
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId])
    if (loading) return <div className="w-full h-24 bg-slate-100 rounded animate-pulse mb-2"></div>
    if (!url) return null
    return <img src={url} alt="" className="w-full max-h-32 object-contain rounded border border-slate-100 mb-2 bg-white" />
}
